import { getIn, setIn, setNestedObjectValues, useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isEqual } from "../util";

const isNestedValue = value =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date);

const getChangedFieldPaths = (values, initialValues, prefix = "") => {
  if (isEqual(values, initialValues)) return [];

  if (!isNestedValue(values) || !isNestedValue(initialValues)) {
    return prefix ? [prefix] : [];
  }

  const keys = [
    ...new Set([...Object.keys(values), ...Object.keys(initialValues)]),
  ];

  return keys.flatMap(key => {
    const path = prefix ? `${prefix}.${key}` : key;
    return getChangedFieldPaths(values?.[key], initialValues?.[key], path);
  });
};

const preserveValuesAtPaths = (nextValues, currentValues, paths) =>
  paths.reduce(
    (values, path) => setIn(values, path, getIn(currentValues, path)),
    nextValues,
  );

const hasValidationErrors = errors => Object.keys(errors || {}).length > 0;

const isExternallySavedPath = (path, externallySavedFields) =>
  externallySavedFields.some(
    field => path === field || path.startsWith(`${field}.`),
  );

const getAutosavedFieldPaths = (
  values,
  savedValues,
  externallySavedFields = [],
) =>
  getChangedFieldPaths(values, savedValues).filter(
    path => !isExternallySavedPath(path, externallySavedFields),
  );

const applyEditedFieldStates = (currentStates, editedPaths, changedPaths) => {
  const nextStates = editedPaths.reduce((states, path) => {
    if (changedPaths.includes(path)) {
      return { ...states, [path]: "pending" };
    }

    return Object.fromEntries(
      Object.entries(states).filter(([statePath]) => statePath !== path),
    );
  }, currentStates);

  return isEqual(nextStates, currentStates) ? currentStates : nextStates;
};

const EMPTY_VALUES = {};

const useAutosaveFormik = ({
  autosaveDelay = 2000,
  externallySavedFields = [],
  initialValues: providedInitialValues,
  savedStateDuration = 1000,
  onSave,
  ...formikOptions
}) => {
  // Records load asynchronously; Formik's setIn throws if its values are ever
  // undefined, so a missing record starts as an empty form.
  const initialValues = providedInitialValues ?? EMPTY_VALUES;
  const formik = useFormik({
    ...formikOptions,
    initialValues,
    enableReinitialize: false,
    onSubmit: () => undefined,
  });
  const [fieldStates, setFieldStates] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const valuesRef = useRef(formik.values);
  const previousValuesRef = useRef(formik.values);
  const savedValuesRef = useRef(formik.values);
  const saveTimerRef = useRef();
  const savedStateTimersRef = useRef({});
  const saveInProgressRef = useRef(false);
  const saveQueuedRef = useRef(false);
  const executeSaveRef = useRef();
  const mountedRef = useRef(true);
  const onSaveRef = useRef(onSave);
  const incomingInitialValuesRef = useRef(initialValues);
  const replacedValuesRef = useRef();
  const renderedValuesRef = useRef(formik.values);
  const validateFormRef = useRef(formik.validateForm);
  const setTouchedRef = useRef(formik.setTouched);
  const formikGetFieldHelpers = formik.getFieldHelpers;
  const formikResetForm = formik.resetForm;
  const formikSetFieldValue = formik.setFieldValue;
  const setFormikValues = formik.setValues;
  const hasValidation = Boolean(
    formikOptions.validationSchema || formikOptions.validate,
  );
  const hasValidationRef = useRef(hasValidation);
  const externallySavedFieldsRef = useRef(externallySavedFields);

  valuesRef.current = formik.values;
  renderedValuesRef.current = formik.values;
  onSaveRef.current = onSave;
  validateFormRef.current = formik.validateForm;
  setTouchedRef.current = formik.setTouched;
  hasValidationRef.current = hasValidation;
  externallySavedFieldsRef.current = externallySavedFields;

  const getPathsToSave = useCallback(
    (values, savedValues) =>
      getAutosavedFieldPaths(
        values,
        savedValues,
        externallySavedFieldsRef.current,
      ),
    [],
  );

  const clearSaveTimer = useCallback(() => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = undefined;
  }, []);

  const clearSavedStateTimer = useCallback(path => {
    clearTimeout(savedStateTimersRef.current[path]);
    delete savedStateTimersRef.current[path];
  }, []);

  const setSuccessfulFieldStates = useCallback(
    (submittedPaths, submittedValues) => {
      const currentValues = valuesRef.current;

      setFieldStates(currentStates =>
        submittedPaths.reduce(
          (nextStates, path) => {
            const hasNewerValue = !isEqual(
              getIn(currentValues, path),
              getIn(submittedValues, path),
            );

            if (hasNewerValue) {
              nextStates[path] = "pending";
            } else {
              nextStates[path] = "saved";
            }

            return nextStates;
          },
          { ...currentStates },
        ),
      );

      submittedPaths.forEach(path => {
        clearSavedStateTimer(path);

        if (isEqual(getIn(currentValues, path), getIn(submittedValues, path))) {
          savedStateTimersRef.current[path] = setTimeout(() => {
            if (!mountedRef.current) return;

            setFieldStates(currentStates => {
              if (currentStates[path] !== "saved") return currentStates;

              const nextStates = { ...currentStates };
              delete nextStates[path];
              return nextStates;
            });
            delete savedStateTimersRef.current[path];
          }, savedStateDuration);
        }
      });
    },
    [clearSavedStateTimer, savedStateDuration],
  );

  const setFailedFieldStates = useCallback(
    (submittedPaths, submittedValues) => {
      const currentValues = valuesRef.current;

      setFieldStates(currentStates =>
        submittedPaths.reduce(
          (nextStates, path) => {
            nextStates[path] = isEqual(
              getIn(currentValues, path),
              getIn(submittedValues, path),
            )
              ? "error"
              : "pending";
            return nextStates;
          },
          { ...currentStates },
        ),
      );
    },
    [],
  );

  const executeSave = useCallback(async () => {
    clearSaveTimer();

    if (!mountedRef.current) return;

    if (saveInProgressRef.current) {
      saveQueuedRef.current = true;
      return;
    }

    const submittedValues = valuesRef.current;
    const previousSavedValues = savedValuesRef.current;
    const submittedPaths = getPathsToSave(submittedValues, previousSavedValues);

    if (submittedPaths.length === 0) {
      setIsSaving(false);
      return;
    }

    saveInProgressRef.current = true;
    setIsSaving(true);
    previousValuesRef.current = submittedValues;
    setFieldStates(currentStates =>
      submittedPaths.reduce(
        (nextStates, path) => ({
          ...nextStates,
          [path]: "saving",
        }),
        currentStates,
      ),
    );

    try {
      const errors = hasValidationRef.current
        ? await validateFormRef.current(submittedValues)
        : {};

      if (hasValidationErrors(errors)) {
        if (mountedRef.current) {
          setTouchedRef.current(setNestedObjectValues(errors, true), false);
          setFailedFieldStates(submittedPaths, submittedValues);
        }
        return;
      }

      await onSaveRef.current?.(submittedValues, previousSavedValues);

      if (!mountedRef.current) return;

      savedValuesRef.current = preserveValuesAtPaths(
        savedValuesRef.current,
        submittedValues,
        submittedPaths,
      );
      setSuccessfulFieldStates(submittedPaths, submittedValues);
    } catch {
      if (mountedRef.current) {
        setFailedFieldStates(submittedPaths, submittedValues);
      }
    } finally {
      saveInProgressRef.current = false;

      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        executeSaveRef.current?.();
      } else if (mountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    clearSaveTimer,
    getPathsToSave,
    setFailedFieldStates,
    setSuccessfulFieldStates,
  ]);

  executeSaveRef.current = executeSave;

  const flushPendingSave = useCallback(() => {
    const submittedValues = valuesRef.current;
    const previousSavedValues = savedValuesRef.current;

    if (getPathsToSave(submittedValues, previousSavedValues).length === 0) {
      return;
    }

    const validation = hasValidationRef.current
      ? validateFormRef.current(submittedValues)
      : Promise.resolve({});

    validation
      .then(errors =>
        hasValidationErrors(errors)
          ? undefined
          : onSaveRef.current?.(submittedValues, previousSavedValues),
      )
      .catch(() => undefined);
  }, [getPathsToSave]);

  const scheduleSave = useCallback(() => {
    clearSaveTimer();
    saveTimerRef.current = setTimeout(executeSave, autosaveDelay);
  }, [autosaveDelay, clearSaveTimer, executeSave]);

  const saveNow = useCallback(async () => {
    clearSaveTimer();
    return executeSave();
  }, [clearSaveTimer, executeSave]);

  const setFieldValue = useCallback(
    (name, value, shouldValidate) => {
      const nextValue =
        typeof value === "function"
          ? value(getIn(valuesRef.current, name))
          : value;
      valuesRef.current = setIn(valuesRef.current, name, nextValue);
      return formikSetFieldValue(name, value, shouldValidate);
    },
    [formikSetFieldValue],
  );

  const resetForm = useCallback(
    nextState => {
      const values =
        nextState?.values ?? savedValuesRef.current ?? EMPTY_VALUES;

      clearSaveTimer();
      Object.keys(savedStateTimersRef.current).forEach(clearSavedStateTimer);
      savedValuesRef.current = values;
      previousValuesRef.current = values;
      valuesRef.current = values;
      setFieldStates({});
      formikResetForm({ ...nextState, values });
    },
    [clearSaveTimer, clearSavedStateTimer, formikResetForm],
  );

  const getFieldHelpers = useCallback(
    name => ({
      ...formikGetFieldHelpers(name),
      setValue: (value, shouldValidate) =>
        setFieldValue(name, value, shouldValidate),
    }),
    [formikGetFieldHelpers, setFieldValue],
  );

  useEffect(() => {
    if (isEqual(initialValues, incomingInitialValuesRef.current)) return;

    const isLoading = incomingInitialValuesRef.current === EMPTY_VALUES;
    incomingInitialValuesRef.current = initialValues;

    const currentValues = valuesRef.current;
    const unsavedPaths = isLoading
      ? []
      : getPathsToSave(currentValues, savedValuesRef.current);
    const nextValues = preserveValuesAtPaths(
      initialValues,
      currentValues,
      unsavedPaths,
    );

    savedValuesRef.current = initialValues;
    previousValuesRef.current = nextValues;
    valuesRef.current = nextValues;
    replacedValuesRef.current = renderedValuesRef.current;
    setFormikValues(nextValues, false);
  }, [getPathsToSave, initialValues, setFormikValues]);

  useEffect(() => {
    // Until Formik renders the reinitialised record, formik.values still holds
    // the values it replaced, which would read as the user editing every field.
    if (formik.values === replacedValuesRef.current) return;
    replacedValuesRef.current = undefined;

    const changedPaths = getPathsToSave(formik.values, savedValuesRef.current);
    const editedPaths = getPathsToSave(
      formik.values,
      previousValuesRef.current,
    );

    previousValuesRef.current = formik.values;

    if (editedPaths.length === 0 || initialValues === EMPTY_VALUES) return;

    editedPaths.forEach(clearSavedStateTimer);
    setFieldStates(currentStates =>
      applyEditedFieldStates(currentStates, editedPaths, changedPaths),
    );

    if (changedPaths.length > 0) {
      scheduleSave();
    } else {
      clearSaveTimer();
    }
  }, [
    clearSaveTimer,
    clearSavedStateTimer,
    formik.values,
    getPathsToSave,
    initialValues,
    scheduleSave,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    const savedStateTimers = savedStateTimersRef.current;

    return () => {
      const hasPendingSave =
        Boolean(saveTimerRef.current) || saveQueuedRef.current;

      mountedRef.current = false;
      clearSaveTimer();
      Object.values(savedStateTimers).forEach(clearTimeout);

      if (hasPendingSave) flushPendingSave();
    };
  }, [clearSaveTimer, flushPendingSave]);

  const autosave = useMemo(
    () => {
      const states = Object.values(fieldStates);
      const formState = states.includes("error")
        ? "error"
        : isSaving
          ? "saving"
          : states.includes("pending")
            ? "editing"
            : states.includes("saved")
              ? "saved"
              : "idle";

      return {
        fieldStates,
        formState,
        getFieldState: name => fieldStates[name],
        saveNow,
      };
    },
    [fieldStates, isSaving, saveNow],
  );

  return {
    ...formik,
    autosave,
    getFieldHelpers,
    handleSubmit: event => {
      event?.preventDefault?.();
      return saveNow();
    },
    resetForm,
    setFieldValue,
    submitForm: saveNow,
    values: formik.values,
  };
};

export {
  applyEditedFieldStates,
  getAutosavedFieldPaths,
  getChangedFieldPaths,
  preserveValuesAtPaths,
};
export default useAutosaveFormik;
